'use client';

import { useState } from 'react';
import { cn } from "@/doi-tac-module/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { QrCode, Copy, Download, Image as ImageIcon, Printer, ChevronDown } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useToast } from '@/doi-tac-module/hooks/use-toast';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { useHasMounted } from '@/doi-tac-module/hooks/use-has-mounted';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

// A perfectly transparent 1x1 GIF for excavate trick
const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

interface AgencyQrDialogProps {
    agencyId: string;
}

export function AgencyQrDialog({ agencyId }: AgencyQrDialogProps) {
    const { language } = useLanguage();
    const { toast } = useToast();
    const { isMasterAgent } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedSize, setSelectedSize] = useState('A4');
    const [selectedTheme, setSelectedTheme] = useState('dark');
    const [activeTab, setActiveTab] = useState('enterprise');
    const hasMounted = useHasMounted();

    // Quick access to translations
    const t = agencyTranslations.qrInvitation;

    // Dynamic base URL for QR link - use window only if mounted
    const shareUrl = hasMounted
        ? `${window.location.origin}/quy-trinh?ref=${agencyId}`
        : `https://hellojob.jp/quy-trinh?ref=${agencyId}`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast({
                title: t.copiedTitle[language],
                description: t.copiedDesc[language],
                className: "bg-green-500 text-white"
            });
        } catch (err) {
            console.error(err);
        }
    };

    const handleDownloadQRCode = () => {
        const svg = document.getElementById('agency-qr-code-svg');
        if (!svg) return;

        // Serialize pure SVG logic inside QRCodeSVG (no HTML)
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement("canvas");
        // Multiply size by 2 for higher resolution
        const scale = 2;
        canvas.width = 220 * scale;
        canvas.height = 220 * scale;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            // Fill background white
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            // Draw SVG
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            // Draw central logo programmatically onto canvas
            const centerImage = new Image();
            centerImage.crossOrigin = "anonymous";
            centerImage.onload = () => {
                // Determine size inside the white excavated square
                // The square width/height is ~ 54px in original (220/4 ~ 54px)
                // With scale = 2, square is 108px.
                const sqSize = 54 * scale;
                // Add a cute border radius effect by drawing a rounded rect
                ctx.save();
                ctx.beginPath();
                const xSq = (canvas.width - sqSize) / 2;
                const ySq = (canvas.height - sqSize) / 2;
                ctx.roundRect(xSq, ySq, sqSize, sqSize, 8 * scale);
                ctx.fillStyle = "white";
                ctx.fill();
                ctx.strokeStyle = "#f1f5f9"; // slate-100
                ctx.lineWidth = 1 * scale;
                ctx.stroke();
                ctx.clip();

                // Draw actual logo matching CSS (44px) -> scaled to 88px inside square
                const targetW = 44 * scale;
                const targetH = targetW * (centerImage.height / centerImage.width);
                const dx = (canvas.width - targetW) / 2;
                const dy = (canvas.height - targetH) / 2;
                ctx.drawImage(centerImage, dx, dy, targetW, targetH);
                ctx.restore();

                // Proceed to download
                const pngFile = canvas.toDataURL("image/png");
                const downloadLink = document.createElement("a");
                downloadLink.download = `hellojob_agency_${agencyId}_qr.png`;
                downloadLink.href = `${pngFile}`;
                downloadLink.click();

                toast({
                    title: t.downloadSuccess[language],
                    description: t.qrSuccess[language],
                    className: "bg-green-500 text-white"
                });
            };
            centerImage.onerror = () => {
                // If logo fails, still allow downloading base QR
                const pngFile = canvas.toDataURL("image/png");
                const downloadLink = document.createElement("a");
                downloadLink.download = `hellojob_agency_${agencyId}_qr.png`;
                downloadLink.href = `${pngFile}`;
                downloadLink.click();
            };
            centerImage.src = "/img/HJPNG.png";
        };
        img.onerror = (e) => {
            console.error("QR image render error", e);
            toast({
                title: t.errorTitle[language],
                description: t.errorDesc[language],
                variant: 'destructive',
            });
        }
        img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    };

    const handlePrintPoster = () => {
        // Map theme to color for URL
        const themeToColor: Record<string, string> = {
            'light': '#ffffff',
            'dark': '#111827',
            'orange': '#F2B92A',
            'green': '#AFC536',
            'blue': '#0D8DC8',
            'lightBlue': '#19A6DF'
        };
        const color = themeToColor[selectedTheme] || '#111827';
        window.open(`/doi-tac/poster/${agencyId}?size=${selectedSize}&color=${encodeURIComponent(color)}`, '_blank');
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogTrigger asChild>
                    <div className="flex md:block cursor-pointer" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="rounded-full hover:bg-slate-100 transition-colors pointer-events-none md:pointer-events-auto md:w-10">
                            <QrCode className="h-6 w-6 text-slate-600" />
                            <span className="sr-only">{t.title[language]}</span>
                        </Button>
                    </div>
                </DialogTrigger>
                <DialogContent className="w-full max-w-none h-full top-0 left-0 translate-x-0 translate-y-0 flex flex-col p-0 gap-0 sm:max-w-md sm:h-auto sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl sm:max-h-[95vh] sm:p-0 overflow-hidden shadow-2xl border-none">
                    <div className="flex flex-col h-full overflow-y-auto custom-scrollbar bg-white">
                        <DialogHeader className="p-3 pb-0 sm:p-6 sm:pb-2 shrink-0">
                            {isMasterAgent ? (
                                <div className="flex flex-col items-center gap-6 pt-4 sm:pt-0">
                                    <DialogTitle className="text-xl sm:text-2xl font-headline text-center text-slate-900 border-b-2 border-primary/20 pb-2">
                                        {t.title[language]}
                                    </DialogTitle>
                                    
                                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                                        <div className="flex justify-center mb-2 sm:mb-3">
                                            <TabsList className="grid w-fit grid-cols-2 p-1 bg-slate-100 rounded-2xl border border-slate-200">
                                                <TabsTrigger 
                                                    value="enterprise" 
                                                    className="rounded-xl px-6 py-2 font-bold transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-lg active:scale-95"
                                                >
                                                    {t.enterpriseTab[language]}
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="agent" 
                                                    className="rounded-xl px-6 py-2 font-bold transition-all data-[state=active]:bg-slate-800 data-[state=active]:text-white data-[state=active]:shadow-lg active:scale-95"
                                                >
                                                    {t.agentTab[language]}
                                                </TabsTrigger>
                                            </TabsList>
                                        </div>
                                        
                                        <div className="min-h-[80px]">
                                            <TabsContent value="enterprise" className="mt-0">
                                                <h3 className="text-base sm:text-lg font-bold text-slate-800 text-center mb-0.5 sm:mb-1">{t.enterpriseTitle[language]}</h3>
                                                <DialogDescription className="text-center text-slate-500 text-[12px] sm:text-base leading-tight">
                                                    {t.enterpriseDesc[language]}
                                                </DialogDescription>
                                            </TabsContent>
                                            <TabsContent value="agent" className="mt-0">
                                                <h3 className="text-base sm:text-lg font-bold text-slate-800 text-center mb-0.5 sm:mb-1">{t.agentTitle[language]}</h3>
                                                <DialogDescription className="text-center text-slate-500 text-[12px] sm:text-base leading-tight">
                                                    {t.agentDesc[language]}
                                                </DialogDescription>
                                            </TabsContent>
                                        </div>
                                    </Tabs>
                                </div>
                            ) : (
                                <>
                                    <DialogTitle className="text-lg sm:text-2xl font-headline text-center text-slate-900 pt-1 sm:pt-0">
                                        {t.enterpriseTitle[language]}
                                    </DialogTitle>
                                    <DialogDescription className="text-center text-slate-500 text-[12px] sm:text-base mt-0.5 sm:mt-1">
                                        {t.enterpriseDesc[language]}
                                    </DialogDescription>
                                </>
                            )}
                        </DialogHeader>

                        <div className="flex-1 px-4 pb-4 sm:px-6 sm:pb-4 space-y-3 sm:space-y-4">
                            <div className="flex flex-col items-center justify-center pt-1 pb-1 space-y-2 sm:space-y-3">
                                <div className="bg-white p-2 sm:p-3 rounded-xl sm:rounded-[1.5rem] border-2 border-slate-50 shadow-lg relative flex items-center justify-center origin-center transform transition-all hover:scale-[1.03]">
                                    {/* Pure SVG wrapper for safe canvas rendering! (No foreign object!) */}
                                    <svg id="agency-qr-code-svg" width="140" height="140" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg" className="sm:w-[180px] sm:h-[180px]">
                                        {hasMounted && (
                                            <QRCodeSVG
                                                value={shareUrl}
                                                size={220}
                                                level={"H"}
                                                includeMargin={false}
                                                imageSettings={{
                                                    src: TRANSPARENT_PIXEL,
                                                    height: 54,
                                                    width: 54,
                                                    excavate: true,
                                                }}
                                            />
                                        )}
                                    </svg>
                                    {/* The HTML Logo sits strictly ABOVE the SVG completely structurally separated. */}
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                        <div className="bg-white flex items-center justify-center w-[34px] h-[34px] sm:w-[44px] sm:h-[44px] rounded-lg shadow-sm border border-slate-100 p-1">
                                            <img src="/img/HJPNG.png" alt="logo" className="w-full h-auto object-contain block" />
                                        </div>
                                    </div>
                                </div>
                                <p className="text-primary font-bold tracking-[0.25em] bg-primary/5 border border-primary/10 px-4 py-1.5 rounded-full uppercase text-[10px] shadow-sm">
                                    ID: {agencyId}
                                </p>
                            </div>

                                <div className="flex flex-col space-y-3 sm:space-y-4">
                                {/* Primary Buttons */}
                                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                                    <Button onClick={handleCopy} variant="outline" className="w-full h-10 sm:h-11 rounded-xl border-slate-200 hover:bg-slate-50 font-bold transition-all active:scale-95 shadow-sm group text-[11px] sm:text-sm">
                                        <Copy className="mr-1.5 h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4 text-slate-400 group-hover:text-primary transition-colors" /> {t.copyLink[language]}
                                    </Button>
                                    <Button onClick={handleDownloadQRCode} variant="outline" className="w-full h-10 sm:h-11 rounded-xl border-primary/30 text-primary hover:bg-primary/5 font-bold transition-all active:scale-95 shadow-sm text-[11px] sm:text-sm">
                                        <Download className="mr-1.5 h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" /> {t.downloadQR[language]}
                                    </Button>
                                </div>

                                <div className="bg-slate-50/80 backdrop-blur-md p-3 sm:p-5 rounded-2xl border border-slate-200/60 space-y-3 sm:space-y-5 shadow-inner">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5 sm:space-y-2">
                                            <label className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.1em] text-slate-400 px-1">{t.selectSize[language]}</label>
                                            <Select value={selectedSize} onValueChange={setSelectedSize}>
                                                <SelectTrigger className="h-9 sm:h-11 bg-white border-slate-100 rounded-xl font-bold shadow-sm focus:ring-primary/20 text-xs sm:text-sm">
                                                    <SelectValue placeholder={t.selectSize[language]} />
                                                </SelectTrigger>
                                                <SelectContent className="rounded-xl border-slate-100 shadow-2xl">
                                                    <SelectItem value="A4" className="font-bold rounded-lg text-xs sm:text-sm">{t.sizes.a4}</SelectItem>
                                                    <SelectItem value="A3" className="font-bold rounded-lg text-xs sm:text-sm">{t.sizes.a3}</SelectItem>
                                                    <SelectItem value="A2" className="font-bold rounded-lg text-xs sm:text-sm">{t.sizes.a2}</SelectItem>
                                                    <SelectItem value="A1" className="font-bold rounded-lg text-xs sm:text-sm">{t.sizes.a1}</SelectItem>
                                                    <SelectItem value="A0" className="font-bold rounded-lg text-xs sm:text-sm">{t.sizes.a0}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1.5 sm:space-y-2">
                                            <label className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.1em] text-slate-400 px-1">{t.selectColor[language]}</label>
                                            <div className="flex items-center justify-start gap-1 sm:gap-1.5 h-9 sm:h-11 px-0.5">
                                                {[
                                                    { id: 'light', hex: '#ffffff', label: t.themes.light[language] },
                                                    { id: 'dark', hex: '#111827', label: t.themes.dark[language] },
                                                    { id: 'orange', hex: '#F2B92A', label: t.themes.orange[language] },
                                                    { id: 'green', hex: '#AFC536', label: t.themes.green[language] },
                                                    { id: 'blue', hex: '#0D8DC8', label: t.themes.blue[language] },
                                                    { id: 'lightBlue', hex: '#19A6DF', label: t.themes.lightBlue[language] },
                                                ].map((theme) => (
                                                    <button
                                                        key={theme.id}
                                                        onClick={() => setSelectedTheme(theme.id)}
                                                        className={cn(
                                                            "w-[21px] h-[21px] sm:w-[26px] sm:h-[26px] rounded-full border-2 transition-all flex-shrink-0 relative overflow-hidden shadow-sm ring-1 ring-slate-200/50",
                                                            selectedTheme === theme.id ? "border-primary scale-110 shadow-md ring-2 ring-primary/20 z-10" : "border-white hover:scale-105"
                                                        )}
                                                        style={{ backgroundColor: theme.hex }}
                                                        title={theme.label}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <Button
                                        onClick={handlePrintPoster}
                                        className="w-full h-10 sm:h-12 bg-primary hover:bg-primary/90 text-white shadow-lg rounded-xl font-black text-sm sm:text-lg group transition-all active:scale-95"
                                    >
                                        <Printer className="mr-1.5 h-4 w-4 sm:mr-2 sm:h-5 sm:w-5 group-hover:rotate-12 transition-transform" />
                                        {t.printPoster[language]}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>


        </>
    );
}
